import { fetchAuthSession } from "aws-amplify/auth";

const API_URL = process.env.REACT_APP_API_URL || "";

export const isApiConfigured = (): boolean => Boolean(API_URL);

export async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token || null;
  } catch {
    return null;
  }
}

export async function getUser(): Promise<Record<string, unknown>> {
  if (!API_URL) throw new Error("REACT_APP_API_URL not set");
  const token = await getAuthToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_URL}/user`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
  return res.json();
}

export async function putUser(data: Record<string, unknown>): Promise<Record<string, unknown>> {
  if (!API_URL) throw new Error("REACT_APP_API_URL not set");
  const token = await getAuthToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_URL}/user`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
  return res.json();
}

export async function patchUser(data: Record<string, unknown>): Promise<Record<string, unknown>> {
  if (!API_URL) throw new Error("REACT_APP_API_URL not set");
  const token = await getAuthToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_URL}/user`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
  return res.json();
}

export type CalculationType = "affordability" | "bond" | "tax" | "monthlyFinances";

export type SavedCalculationEntry = {
  calculationId: string;
  calculationType: CalculationType;
  createdAt: string;
  name: string;
  payload?: Record<string, unknown>;
};

export async function createCalculation(params: {
  calculationType: CalculationType;
  name?: string;
  payload: Record<string, unknown>;
}): Promise<SavedCalculationEntry & { payload?: Record<string, unknown> }> {
  if (!API_URL) throw new Error("REACT_APP_API_URL not set");
  const token = await getAuthToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_URL}/calculations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
  return res.json();
}

export async function listCalculations(): Promise<{ items: SavedCalculationEntry[] }> {
  if (!API_URL) throw new Error("REACT_APP_API_URL not set");
  const token = await getAuthToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_URL}/calculations`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
  return res.json();
}

export async function getCalculation(
  calculationId: string
): Promise<SavedCalculationEntry & { payload?: Record<string, unknown> }> {
  if (!API_URL) throw new Error("REACT_APP_API_URL not set");
  const token = await getAuthToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_URL}/calculations/${encodeURIComponent(calculationId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
  return res.json();
}

export async function updateCalculation(
  calculationId: string,
  params: { name?: string; payload?: Record<string, unknown> }
): Promise<SavedCalculationEntry & { payload?: Record<string, unknown> }> {
  if (!API_URL) throw new Error("REACT_APP_API_URL not set");
  const token = await getAuthToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_URL}/calculations/${encodeURIComponent(calculationId)}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
  return res.json();
}

export async function deleteCalculation(calculationId: string): Promise<void> {
  if (!API_URL) throw new Error("REACT_APP_API_URL not set");
  const token = await getAuthToken();
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(`${API_URL}/calculations/${encodeURIComponent(calculationId)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
}
